import React, { useState, useEffect } from "react";
import {
  Library,
  Search,
  Upload,
  Download,
  BookOpen,
  FileText,
  Video,
  Image,
  Music,
  Star,
  Bookmark,
  Eye,
  Share,
  MoreVertical,
  Play,
  Check,
} from "lucide-react";
import "./ELibrary.css";

const ELibrary = () => {
  const [activeTab, setActiveTab] = useState("search");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");
  const [selectedResource, setSelectedResource] = useState(null);
  const [activeLibraryTab, setActiveLibraryTab] = useState("bookmarked");
  const [resources, setResources] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    author: "",
    category: "Mathematics",
    grade: "All Grades",
    description: "",
    tags: "",
    accessLevel: "Public - All students",
  });
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);

  // Initialize resources state with mock data
  useEffect(() => {
    const initialResources = [
      {
        id: 1,
        title: "Advanced Mathematics Textbook",
        author: "Dr. Sarah Johnson",
        type: "ebook",
        category: "Mathematics",
        grade: "Grade 10-12",
        size: "15.2 MB",
        pages: 450,
        rating: 4.8,
        downloads: 1234,
        uploadDate: "2024-01-10",
        description:
          "Comprehensive mathematics textbook covering advanced topics including calculus, algebra, and statistics.",
        tags: ["calculus", "algebra", "statistics", "advanced"],
        fileUrl: "/ebooks/mathematics-advanced.pdf",
        thumbnail: "📚",
        isBookmarked: false,
        isDownloaded: false,
      },
      {
        id: 2,
        title: "Physics Lab Experiments Video",
        author: "Prof. Michael Chen",
        type: "video",
        category: "Science",
        grade: "Grade 9-11",
        size: "245.8 MB",
        duration: "45:30",
        rating: 4.6,
        downloads: 856,
        uploadDate: "2024-01-08",
        description:
          "Step-by-step video guide for conducting physics laboratory experiments safely and effectively.",
        tags: ["physics", "experiments", "lab", "video"],
        fileUrl: "/videos/physics-lab-experiments.mp4",
        thumbnail: "🎥",
        isBookmarked: true,
        isDownloaded: true,
      },
      {
        id: 3,
        title: "Chemistry Periodic Table Interactive",
        author: "Dr. Emily Rodriguez",
        type: "interactive",
        category: "Science",
        grade: "All Grades",
        size: "8.7 MB",
        rating: 4.9,
        downloads: 2103,
        uploadDate: "2024-01-05",
        description:
          "Interactive periodic table with detailed information about each element, including properties and uses.",
        tags: ["chemistry", "periodic-table", "interactive", "elements"],
        fileUrl: "/interactive/periodic-table.html",
        thumbnail: "⚗️",
        isBookmarked: false,
        isDownloaded: false,
      },
      {
        id: 4,
        title: "World History Timeline",
        author: "Dr. James Wilson",
        type: "image",
        category: "History",
        grade: "Grade 6-12",
        size: "12.3 MB",
        resolution: "4000x3000",
        rating: 4.7,
        downloads: 987,
        uploadDate: "2024-01-03",
        description:
          "High-resolution timeline of world history from ancient civilizations to modern times.",
        tags: ["history", "timeline", "world", "civilizations"],
        fileUrl: "/images/world-history-timeline.jpg",
        thumbnail: "🗺️",
        isBookmarked: true,
        isDownloaded: false,
      },
      {
        id: 5,
        title: "English Literature Audio Book",
        author: "Shakespeare",
        type: "audio",
        category: "Literature",
        grade: "Grade 9-12",
        size: "89.4 MB",
        duration: "3:45:20",
        rating: 4.5,
        downloads: 1456,
        uploadDate: "2024-01-01",
        description:
          "Complete audio recording of Shakespeare's Hamlet with professional narration.",
        tags: ["shakespeare", "hamlet", "literature", "audio"],
        fileUrl: "/audio/hamlet-audiobook.mp3",
        thumbnail: "🎭",
        isBookmarked: false,
        isDownloaded: true,
      },
      {
        id: 6,
        title: "Calculus Integration Techniques",
        author: "Dr. Maria Santos",
        type: "ebook",
        category: "Mathematics",
        grade: "Grade 11-12",
        size: "22.1 MB",
        pages: 320,
        rating: 4.9,
        downloads: 2103,
        uploadDate: "2024-01-12",
        description:
          "Comprehensive guide to integration techniques including substitution, parts, and partial fractions with step-by-step examples.",
        tags: ["calculus", "integration", "derivatives", "mathematics"],
        fileUrl: "/ebooks/calculus-integration.pdf",
        thumbnail: "📐",
        isBookmarked: false,
        isDownloaded: false,
      },
      {
        id: 7,
        title: "Photosynthesis Process Animation",
        author: "Dr. Lisa Chen",
        type: "video",
        category: "Science",
        grade: "Grade 8-10",
        size: "156.7 MB",
        duration: "12:45",
        rating: 4.7,
        downloads: 1876,
        uploadDate: "2024-01-11",
        description:
          "Animated explanation of the photosynthesis process with 3D molecular models and interactive diagrams.",
        tags: ["photosynthesis", "biology", "plants", "energy", "animation"],
        fileUrl: "/videos/photosynthesis-animation.mp4",
        thumbnail: "🌿",
        isBookmarked: true,
        isDownloaded: false,
      },
      {
        id: 8,
        title: "World War II Interactive Timeline",
        author: "Prof. David Miller",
        type: "interactive",
        category: "History",
        grade: "Grade 9-12",
        size: "45.2 MB",
        rating: 4.8,
        downloads: 3245,
        uploadDate: "2024-01-09",
        description:
          "Interactive timeline of World War II with detailed events, maps, and multimedia content for comprehensive learning.",
        tags: ["world war 2", "timeline", "history", "interactive", "war"],
        fileUrl: "/interactive/ww2-timeline.html",
        thumbnail: "⚔️",
        isBookmarked: false,
        isDownloaded: true,
      },
      {
        id: 9,
        title: "Shakespeare Sonnet Analysis Guide",
        author: "Dr. Emily Watson",
        type: "ebook",
        category: "Literature",
        grade: "Grade 10-12",
        size: "18.9 MB",
        pages: 280,
        rating: 4.6,
        downloads: 1456,
        uploadDate: "2024-01-07",
        description:
          "Detailed analysis of Shakespeare's sonnets with literary devices, themes, and historical context explanations.",
        tags: ["shakespeare", "sonnets", "poetry", "analysis", "literature"],
        fileUrl: "/ebooks/shakespeare-sonnets.pdf",
        thumbnail: "📜",
        isBookmarked: true,
        isDownloaded: false,
      },
    ];
    setResources(initialResources);
  }, []);

  const categories = [
    { id: "all", label: "All Resources", icon: Library },
    { id: "Mathematics", label: "Mathematics", icon: BookOpen },
    { id: "Science", label: "Science", icon: FileText },
    { id: "Literature", label: "Literature", icon: BookOpen },
    { id: "History", label: "History", icon: FileText },
    { id: "Languages", label: "Languages", icon: BookOpen },
  ];

  const resourceTypes = [
    { id: "ebook", label: "E-books", icon: BookOpen },
    { id: "video", label: "Videos", icon: Video },
    { id: "audio", label: "Audio", icon: Music },
    { id: "image", label: "Images", icon: Image },
    { id: "interactive", label: "Interactive", icon: Play },
  ];

  const tabs = [
    { id: "search", label: "AI Search", icon: Search },
    { id: "browse", label: "Browse Library", icon: Library },
    { id: "upload", label: "Upload Content", icon: Upload },
    { id: "my-library", label: "My Library", icon: Bookmark },
  ];

  // AI-powered search with semantic matching
  const calculateRelevanceScore = (resource, searchTerm) => {
    if (!searchTerm) return 0;

    const term = searchTerm.toLowerCase();
    let score = 0;

    // Title exact match (highest priority)
    if (resource.title.toLowerCase().includes(term)) {
      score += 100;
      if (resource.title.toLowerCase().startsWith(term)) score += 50;
    }

    // Author match
    if (resource.author.toLowerCase().includes(term)) {
      score += 80;
    }

    // Tag matches
    const tagMatches = resource.tags.filter((tag) =>
      tag.toLowerCase().includes(term)
    ).length;
    score += tagMatches * 30;

    // Description match
    if (resource.description.toLowerCase().includes(term)) {
      score += 20;
    }

    // Semantic matching (simplified)
    const semanticMatches = {
      math: ["mathematics", "calculus", "algebra", "geometry", "statistics"],
      science: ["physics", "chemistry", "biology", "experiments", "lab"],
      history: ["timeline", "civilizations", "war", "ancient", "world"],
      literature: ["shakespeare", "poetry", "novel", "story", "book"],
      interactive: ["periodic", "table", "elements", "chemistry"],
    };

    Object.entries(semanticMatches).forEach(([key, synonyms]) => {
      if (term.includes(key) || synonyms.some((syn) => term.includes(syn))) {
        if (
          resource.category.toLowerCase().includes(key) ||
          resource.tags.some((tag) => synonyms.includes(tag.toLowerCase()))
        ) {
          score += 40;
        }
      }
    });

    return score;
  };

  // Filter and sort resources with AI relevance scoring
  const filteredResources = resources
    .map((resource) => ({
      ...resource,
      relevanceScore: calculateRelevanceScore(resource, searchTerm),
    }))
    .filter((resource) => {
      const matchesSearch = searchTerm === "" || resource.relevanceScore > 0;
      const matchesCategory =
        selectedCategory === "all" || resource.category === selectedCategory;
      const matchesType =
        selectedType === "all" || resource.type === selectedType;
      return matchesSearch && matchesCategory && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date":
          return new Date(b.uploadDate) - new Date(a.uploadDate);
        case "rating":
          return b.rating - a.rating;
        case "downloads":
          return b.downloads - a.downloads;
        case "relevance":
        default:
          return b.relevanceScore - a.relevanceScore;
      }
    });

  const handleBookmark = (resourceId) => {
    setResources((prevResources) =>
      prevResources.map((resource) =>
        resource.id === resourceId
          ? { ...resource, isBookmarked: !resource.isBookmarked }
          : resource
      )
    );
  };

  const handleDownload = (resource) => {
    setResources((prevResources) =>
      prevResources.map((r) =>
        r.id === resource.id
          ? { ...r, isDownloaded: true, downloads: r.downloads + 1 }
          : r
      )
    );
    // Simulate download
    const link = document.createElement("a");
    link.href = resource.fileUrl;
    link.download = resource.title;
    link.click();
  };

  // Generate AI-powered search suggestions
  const generateSearchSuggestions = (term) => {
    if (!term || term.length < 2) return [];

    const suggestions = [];
    const lowerTerm = term.toLowerCase();

    // Generate suggestions based on existing content
    resources.forEach((resource) => {
      // Title suggestions
      if (resource.title.toLowerCase().includes(lowerTerm)) {
        suggestions.push({
          text: resource.title,
          type: "title",
          category: resource.category,
        });
      }

      // Tag suggestions
      resource.tags.forEach((tag) => {
        if (tag.toLowerCase().includes(lowerTerm)) {
          suggestions.push({
            text: tag,
            type: "tag",
            category: resource.category,
          });
        }
      });
    });

    // AI-generated conceptual suggestions
    const conceptualSuggestions = {
      math: ["derivatives", "integration", "trigonometry", "probability"],
      science: ["photosynthesis", "molecular structure", "energy", "reactions"],
      history: [
        "ancient civilizations",
        "world wars",
        "renaissance",
        "revolution",
      ],
      literature: [
        "poetry analysis",
        "character development",
        "themes",
        "symbolism",
      ],
    };

    Object.entries(conceptualSuggestions).forEach(([category, concepts]) => {
      if (
        lowerTerm.includes(category) ||
        lowerTerm.includes("learn") ||
        lowerTerm.includes("study")
      ) {
        concepts.forEach((concept) => {
          suggestions.push({
            text: `${category} ${concept}`,
            type: "concept",
            category: category,
          });
        });
      }
    });

    return [...new Set(suggestions.map((s) => s.text))].slice(0, 8);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);

    // Add to search history
    setSearchHistory((prev) => {
      const filtered = prev.filter((term) => term !== searchTerm);
      return [searchTerm, ...filtered].slice(0, 10);
    });

    // Generate AI insights
    setTimeout(() => {
      const insights = generateAIInsights(searchTerm, filteredResources);
      setAiInsights(insights);
      setIsSearching(false);
    }, 1000);
  };

  const generateAIInsights = (term, results) => {
    if (!term || results.length === 0) return null;

    const insights = {
      totalResults: results.length,
      topCategories: {},
      recommendations: [],
      relatedConcepts: [],
    };

    // Analyze categories
    results.forEach((resource) => {
      insights.topCategories[resource.category] =
        (insights.topCategories[resource.category] || 0) + 1;
    });

    // Generate recommendations
    if (results.length > 0) {
      const topResult = results[0];
      insights.recommendations.push(
        `"${topResult.title}" is highly relevant to your search`
      );

      if (results.length > 1) {
        insights.recommendations.push(
          `Found ${results.length} resources across ${
            Object.keys(insights.topCategories).length
          } categories`
        );
      }
    }

    // Generate related concepts
    const conceptMap = {
      math: ["algebra", "geometry", "calculus", "statistics"],
      science: ["physics", "chemistry", "biology", "experiments"],
      history: ["timeline", "civilizations", "wars", "culture"],
      literature: ["poetry", "novels", "analysis", "themes"],
    };

    Object.entries(conceptMap).forEach(([category, concepts]) => {
      if (term.toLowerCase().includes(category)) {
        insights.relatedConcepts = concepts.slice(0, 3);
      }
    });

    return insights;
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions as user types
    if (value.length >= 2) {
      const suggestions = generateSearchSuggestions(value);
      setSearchSuggestions(suggestions);
    } else {
      setSearchSuggestions([]);
    }

    // Clear AI insights when search changes
    if (aiInsights) {
      setAiInsights(null);
    }
  };

  const handleSearchInputBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setSearchSuggestions([]);
    }, 200);
  };

  const handleUploadFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.epub,.mp4,.mp3,.jpg,.jpeg,.png,.html";
    input.onchange = (e) => {
      // In a real app, you would handle file upload here
      // const files = Array.from(e.target.files);
    };
    input.click();
  };

  const handleFormChange = (field, value) => {
    setUploadForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveDraft = () => {
    // Save form data to localStorage as draft
    localStorage.setItem("elibrary-draft", JSON.stringify(uploadForm));
    // Use a more user-friendly notification instead of alert
    const notification = document.createElement("div");
    notification.textContent = "Draft saved successfully!";
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-weight: 500;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 3000);
  };

  const handleUploadPublish = () => {
    if (!uploadForm.title || !uploadForm.author) {
      // Use a more user-friendly notification instead of alert
      const notification = document.createElement("div");
      notification.textContent =
        "Please fill in required fields (Title and Author)";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 1rem 1.5rem;
        border-radius: 0.5rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-weight: 500;
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);
      return;
    }

    const newResource = {
      id: Date.now(),
      title: uploadForm.title,
      author: uploadForm.author,
      type: "ebook", // Default type, would be determined by file
      category: uploadForm.category,
      grade: uploadForm.grade,
      size: "0 MB", // Would be calculated from file
      rating: 0,
      downloads: 0,
      uploadDate: new Date().toISOString().split("T")[0],
      description: uploadForm.description,
      tags: uploadForm.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      fileUrl: "#",
      thumbnail: "📄",
      isBookmarked: false,
      isDownloaded: false,
    };

    setResources((prev) => [newResource, ...prev]);
    setUploadForm({
      title: "",
      author: "",
      category: "Mathematics",
      grade: "All Grades",
      description: "",
      tags: "",
      accessLevel: "Public - All students",
    });
    // Use a more user-friendly notification instead of alert
    const notification = document.createElement("div");
    notification.textContent = "Content uploaded and published successfully!";
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-weight: 500;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 3000);
  };

  const handleLibraryTab = (tabName) => {
    setActiveLibraryTab(tabName.toLowerCase().replace(" ", "-"));
  };

  const handleOpenResource = (resource) => {
    setSelectedResource(resource);
    // Add to recently viewed
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((r) => r.id !== resource.id);
      return [resource, ...filtered].slice(0, 10);
    });
  };

  const handleRemoveFromLibrary = (resource) => {
    // Create a custom confirmation modal instead of using window.confirm
    const confirmModal = document.createElement("div");
    confirmModal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background: white;
      padding: 2rem;
      border-radius: 1rem;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      max-width: 400px;
      width: 90%;
      text-align: center;
    `;

    modalContent.innerHTML = `
      <h3 style="margin: 0 0 1rem 0; color: #1e293b;">Confirm Removal</h3>
      <p style="margin: 0 0 2rem 0; color: #64748b;">Remove "${resource.title}" from your library?</p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="cancel-btn" style="
          padding: 0.75rem 1.5rem;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 0.5rem;
          cursor: pointer;
          color: #64748b;
        ">Cancel</button>
        <button id="confirm-btn" style="
          padding: 0.75rem 1.5rem;
          border: 1px solid #ef4444;
          background: #ef4444;
          color: white;
          border-radius: 0.5rem;
          cursor: pointer;
        ">Remove</button>
      </div>
    `;

    confirmModal.appendChild(modalContent);
    document.body.appendChild(confirmModal);

    const cancelBtn = modalContent.querySelector("#cancel-btn");
    const confirmBtn = modalContent.querySelector("#confirm-btn");

    const cleanup = () => {
      document.body.removeChild(confirmModal);
    };

    cancelBtn.addEventListener("click", cleanup);
    confirmBtn.addEventListener("click", () => {
      setResources((prevResources) =>
        prevResources.map((r) =>
          r.id === resource.id
            ? { ...r, isBookmarked: false, isDownloaded: false }
            : r
        )
      );
      cleanup();
    });

    confirmModal.addEventListener("click", (e) => {
      if (e.target === confirmModal) {
        cleanup();
      }
    });
  };

  const handleShareResource = (resource) => {
    if (navigator.share) {
      navigator.share({
        title: resource.title,
        text: resource.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(
        `${resource.title} - ${resource.description}`
      );
      // Use a more user-friendly notification instead of alert
      const notification = document.createElement("div");
      notification.textContent = "Resource link copied to clipboard!";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 1rem 1.5rem;
        border-radius: 0.5rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-weight: 500;
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);
    }
  };

  const handleView = (resource) => {
    setSelectedResource(resource);
    // Add to recently viewed
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((r) => r.id !== resource.id);
      return [resource, ...filtered].slice(0, 10);
    });
  };

  const handleModalBookmark = (resource) => {
    handleBookmark(resource.id);
  };

  const handleModalDownload = (resource) => {
    handleDownload(resource);
  };

  const renderAISearch = () => (
    <div className="ai-search-section">
      <div className="search-header">
        <h3>🤖 AI-Powered Search</h3>
        <p>
          Search by content, concepts, or ask questions about the resources. Our
          AI understands context and provides intelligent suggestions.
        </p>
      </div>

      <div className="ai-search-bar">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Try: 'learn calculus', 'physics experiments', 'world history', or 'shakespeare analysis'..."
            value={searchTerm}
            onChange={handleSearchInputChange}
            onBlur={handleSearchInputBlur}
            className="ai-search-input"
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? (
              <>
                <div className="spinner"></div>
                AI Searching...
              </>
            ) : (
              <>
                <Search size={16} />
                AI Search
              </>
            )}
          </button>
        </div>

        {/* Dynamic Search Suggestions */}
        {searchSuggestions.length > 0 && (
          <div className="dynamic-suggestions">
            <div className="suggestion-dropdown">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <Search size={14} />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Insights Panel */}
      {aiInsights && (
        <div className="ai-insights-panel">
          <div className="insights-header">
            <h4>🧠 AI Insights</h4>
            <span className="insights-badge">Powered by AI</span>
          </div>
          <div className="insights-content">
            <div className="insight-item">
              <strong>Search Analysis:</strong>{" "}
              {aiInsights.recommendations.join(". ")}
            </div>
            {Object.keys(aiInsights.topCategories).length > 0 && (
              <div className="insight-item">
                <strong>Top Categories:</strong>{" "}
                {Object.entries(aiInsights.topCategories)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([category, count]) => `${category} (${count})`)
                  .join(", ")}
              </div>
            )}
            {aiInsights.relatedConcepts.length > 0 && (
              <div className="insight-item">
                <strong>Related Concepts:</strong>{" "}
                {aiInsights.relatedConcepts.join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && (
        <div className={`search-history ${aiInsights ? "with-insights" : ""}`}>
          <h4>Recent Searches:</h4>
          <div className="history-tags">
            {searchHistory.slice(0, 5).map((term, index) => (
              <button
                key={index}
                className="history-tag"
                onClick={() => handleSuggestionClick(term)}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={`search-suggestions ${aiInsights ? "with-insights" : ""}`}
      >
        <h4>💡 Popular AI Searches:</h4>
        <div className="suggestion-tags">
          <button
            className="suggestion-tag ai-tag"
            onClick={() => handleSuggestionClick("learn calculus derivatives")}
          >
            🧮 learn calculus derivatives
          </button>
          <button
            className="suggestion-tag ai-tag"
            onClick={() => handleSuggestionClick("photosynthesis process")}
          >
            🌱 photosynthesis process
          </button>
          <button
            className="suggestion-tag ai-tag"
            onClick={() => handleSuggestionClick("world war 2 timeline")}
          >
            🌍 world war 2 timeline
          </button>
          <button
            className="suggestion-tag ai-tag"
            onClick={() => handleSuggestionClick("shakespeare analysis")}
          >
            🎭 shakespeare analysis
          </button>
          <button
            className="suggestion-tag ai-tag"
            onClick={() => handleSuggestionClick("periodic table interactive")}
          >
            ⚗️ periodic table interactive
          </button>
        </div>
      </div>

      <div className={`search-results ${aiInsights ? "with-insights" : ""}`}>
        <div className="results-header">
          <h4>Search Results ({filteredResources.length})</h4>
          <div className="results-filters">
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="relevance">Sort by Relevance</option>
              <option value="date">Sort by Date</option>
              <option value="rating">Sort by Rating</option>
              <option value="downloads">Sort by Downloads</option>
            </select>
          </div>
        </div>

        <div className="resources-grid">
          {filteredResources.map((resource) => (
            <div key={resource.id} className="resource-card">
              <div className="resource-thumbnail">
                <span className="thumbnail-icon">{resource.thumbnail}</span>
                <div className="resource-type-badge">
                  {resourceTypes.find((type) => type.id === resource.type)
                    ?.icon &&
                    React.createElement(
                      resourceTypes.find((type) => type.id === resource.type)
                        .icon,
                      { size: 16 }
                    )}
                </div>
              </div>

              <div className="resource-info">
                <h4 className="resource-title">
                  {resource.title}
                  {resource.relevanceScore > 0 && (
                    <span className="relevance-badge">
                      {resource.relevanceScore > 100
                        ? "🔥"
                        : resource.relevanceScore > 50
                        ? "⭐"
                        : "✨"}
                      {Math.round(resource.relevanceScore / 10)}% match
                    </span>
                  )}
                </h4>
                <p className="resource-author">by {resource.author}</p>
                <p className="resource-description">{resource.description}</p>

                <div className="resource-meta">
                  <span className="meta-item">
                    <Star size={14} />
                    {resource.rating}
                  </span>
                  <span className="meta-item">
                    <Download size={14} />
                    {resource.downloads}
                  </span>
                  <span className="meta-item">{resource.size}</span>
                </div>

                <div className="resource-tags">
                  {resource.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="resource-actions">
                <button
                  className="action-btn"
                  onClick={() => handleBookmark(resource.id)}
                  title={
                    resource.isBookmarked
                      ? "Remove from bookmarks"
                      : "Add to bookmarks"
                  }
                >
                  {resource.isBookmarked ? (
                    <Check size={16} />
                  ) : (
                    <Bookmark size={16} />
                  )}
                </button>
                <button
                  className="action-btn"
                  onClick={() => handleView(resource)}
                  title="View details"
                >
                  <Eye size={16} />
                </button>
                <button
                  className="action-btn"
                  onClick={() => handleDownload(resource)}
                  title="Download"
                >
                  <Download size={16} />
                </button>
                <button className="action-btn" title="More options">
                  <MoreVertical size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBrowseLibrary = () => (
    <div className="browse-section">
      <div className="browse-header">
        <h3>Browse Digital Library</h3>
        <div className="browse-filters">
          <div className="category-filter">
            <label>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="form-select"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <div className="type-filter">
            <label>Type:</label>
            <select
              className="form-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              {resourceTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="resources-grid">
        {filteredResources.map((resource) => (
          <div key={resource.id} className="resource-card">
            <div className="resource-thumbnail">
              <span className="thumbnail-icon">{resource.thumbnail}</span>
              <div className="resource-type-badge">
                {resourceTypes.find((type) => type.id === resource.type)
                  ?.icon &&
                  React.createElement(
                    resourceTypes.find((type) => type.id === resource.type)
                      .icon,
                    { size: 16 }
                  )}
              </div>
            </div>

            <div className="resource-info">
              <h4 className="resource-title">{resource.title}</h4>
              <p className="resource-author">by {resource.author}</p>
              <p className="resource-description">{resource.description}</p>

              <div className="resource-meta">
                <span className="meta-item">
                  <Star size={14} />
                  {resource.rating}
                </span>
                <span className="meta-item">
                  <Download size={14} />
                  {resource.downloads}
                </span>
                <span className="meta-item">{resource.size}</span>
              </div>

              <div className="resource-tags">
                {resource.tags.slice(0, 3).map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="resource-actions">
              <button
                className="action-btn"
                onClick={() => handleBookmark(resource.id)}
                title={
                  resource.isBookmarked
                    ? "Remove from bookmarks"
                    : "Add to bookmarks"
                }
              >
                {resource.isBookmarked ? (
                  <Check size={16} />
                ) : (
                  <Bookmark size={16} />
                )}
              </button>
              <button
                className="action-btn"
                onClick={() => handleView(resource)}
                title="View details"
              >
                <Eye size={16} />
              </button>
              <button
                className="action-btn"
                onClick={() => handleDownload(resource)}
                title="Download"
              >
                <Download size={16} />
              </button>
              <button className="action-btn" title="More options">
                <MoreVertical size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderUploadContent = () => (
    <div className="upload-section">
      <div className="upload-header">
        <h3>Upload Digital Content</h3>
        <p>Share educational resources with the school community</p>
      </div>

      <div className="upload-form">
        <div className="upload-area">
          <div className="upload-icon">
            <Upload size={48} />
          </div>
          <h4>Drag & Drop files here</h4>
          <p>or click to browse</p>
          <button className="btn btn-primary" onClick={handleUploadFiles}>
            Choose Files
          </button>
          <div className="upload-formats">
            <p>Supported formats: PDF, EPUB, MP4, MP3, JPG, PNG, HTML</p>
          </div>
        </div>

        <div className="upload-details">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter resource title"
              value={uploadForm.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Author/Creator *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter author name"
              value={uploadForm.author}
              onChange={(e) => handleFormChange("author", e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select
                className="form-select"
                value={uploadForm.category}
                onChange={(e) => handleFormChange("category", e.target.value)}
              >
                <option value="Mathematics">Mathematics</option>
                <option value="Science">Science</option>
                <option value="Literature">Literature</option>
                <option value="History">History</option>
                <option value="Languages">Languages</option>
              </select>
            </div>
            <div className="form-group">
              <label>Grade Level</label>
              <select
                className="form-select"
                value={uploadForm.grade}
                onChange={(e) => handleFormChange("grade", e.target.value)}
              >
                <option value="All Grades">All Grades</option>
                <option value="Grade 6-8">Grade 6-8</option>
                <option value="Grade 9-12">Grade 9-12</option>
                <option value="Grade 10-12">Grade 10-12</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-input"
              rows="4"
              placeholder="Describe the resource content and learning objectives"
              value={uploadForm.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              className="form-input"
              placeholder="calculus, algebra, mathematics"
              value={uploadForm.tags}
              onChange={(e) => handleFormChange("tags", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Access Level</label>
            <select
              className="form-select"
              value={uploadForm.accessLevel}
              onChange={(e) => handleFormChange("accessLevel", e.target.value)}
            >
              <option value="Public - All students">
                Public - All students
              </option>
              <option value="Grade-specific">Grade-specific</option>
              <option value="Teacher-only">Teacher-only</option>
            </select>
          </div>
        </div>
      </div>

      <div className="upload-actions">
        <button className="btn btn-outline" onClick={handleSaveDraft}>
          Save as Draft
        </button>
        <button className="btn btn-primary" onClick={handleUploadPublish}>
          Upload & Publish
        </button>
      </div>
    </div>
  );

  const renderMyLibrary = () => (
    <div className="my-library-section">
      <div className="library-header">
        <h3>My Digital Library</h3>
        <div className="library-stats">
          <div className="stat-item">
            <span className="stat-number">
              {resources.filter((r) => r.isBookmarked).length}
            </span>
            <span className="stat-label">Bookmarked</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {resources.filter((r) => r.isDownloaded).length}
            </span>
            <span className="stat-label">Downloaded</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {
                resources.filter(
                  (r) => r.uploadDate === new Date().toISOString().split("T")[0]
                ).length
              }
            </span>
            <span className="stat-label">Uploaded</span>
          </div>
        </div>
      </div>

      <div className="library-tabs">
        <button
          className={`library-tab ${
            activeLibraryTab === "bookmarked" ? "active" : ""
          }`}
          onClick={() => handleLibraryTab("Bookmarked")}
        >
          Bookmarked
        </button>
        <button
          className={`library-tab ${
            activeLibraryTab === "downloaded" ? "active" : ""
          }`}
          onClick={() => handleLibraryTab("Downloaded")}
        >
          Downloaded
        </button>
        <button
          className={`library-tab ${
            activeLibraryTab === "recently-viewed" ? "active" : ""
          }`}
          onClick={() => handleLibraryTab("Recently Viewed")}
        >
          Recently Viewed
        </button>
        <button
          className={`library-tab ${
            activeLibraryTab === "my-uploads" ? "active" : ""
          }`}
          onClick={() => handleLibraryTab("My Uploads")}
        >
          My Uploads
        </button>
      </div>

      <div className="library-content">
        {activeLibraryTab === "bookmarked" && (
          <div className="bookmarked-resources">
            {resources
              .filter((r) => r.isBookmarked)
              .map((resource) => (
                <div key={resource.id} className="library-item">
                  <div className="item-thumbnail">
                    <span className="thumbnail-icon">{resource.thumbnail}</span>
                  </div>
                  <div className="item-info">
                    <h4>{resource.title}</h4>
                    <p>by {resource.author}</p>
                    <div className="item-meta">
                      <span>{resource.category}</span>
                      <span>•</span>
                      <span>{resource.size}</span>
                      <span>•</span>
                      <span>Added {resource.uploadDate}</span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleOpenResource(resource)}
                    >
                      Open
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleRemoveFromLibrary(resource)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            {resources.filter((r) => r.isBookmarked).length === 0 && (
              <div className="empty-state">
                <p>
                  No bookmarked resources yet. Start bookmarking resources you
                  find interesting!
                </p>
              </div>
            )}
          </div>
        )}

        {activeLibraryTab === "downloaded" && (
          <div className="downloaded-resources">
            {resources
              .filter((r) => r.isDownloaded)
              .map((resource) => (
                <div key={resource.id} className="library-item">
                  <div className="item-thumbnail">
                    <span className="thumbnail-icon">{resource.thumbnail}</span>
                  </div>
                  <div className="item-info">
                    <h4>{resource.title}</h4>
                    <p>by {resource.author}</p>
                    <div className="item-meta">
                      <span>{resource.category}</span>
                      <span>•</span>
                      <span>{resource.size}</span>
                      <span>•</span>
                      <span>Downloaded {resource.uploadDate}</span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleOpenResource(resource)}
                    >
                      Open
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleDownload(resource)}
                    >
                      Re-download
                    </button>
                  </div>
                </div>
              ))}
            {resources.filter((r) => r.isDownloaded).length === 0 && (
              <div className="empty-state">
                <p>
                  No downloaded resources yet. Download resources to access them
                  offline!
                </p>
              </div>
            )}
          </div>
        )}

        {activeLibraryTab === "recently-viewed" && (
          <div className="recently-viewed-resources">
            {recentlyViewed.map((resource) => (
              <div key={resource.id} className="library-item">
                <div className="item-thumbnail">
                  <span className="thumbnail-icon">{resource.thumbnail}</span>
                </div>
                <div className="item-info">
                  <h4>{resource.title}</h4>
                  <p>by {resource.author}</p>
                  <div className="item-meta">
                    <span>{resource.category}</span>
                    <span>•</span>
                    <span>{resource.size}</span>
                    <span>•</span>
                    <span>Recently viewed</span>
                  </div>
                </div>
                <div className="item-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenResource(resource)}
                  >
                    Open
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handleBookmark(resource.id)}
                  >
                    {resource.isBookmarked ? "Unbookmark" : "Bookmark"}
                  </button>
                </div>
              </div>
            ))}
            {recentlyViewed.length === 0 && (
              <div className="empty-state">
                <p>
                  No recently viewed resources. Start exploring the library!
                </p>
              </div>
            )}
          </div>
        )}

        {activeLibraryTab === "my-uploads" && (
          <div className="my-uploads-resources">
            {resources
              .filter(
                (r) => r.uploadDate === new Date().toISOString().split("T")[0]
              )
              .map((resource) => (
                <div key={resource.id} className="library-item">
                  <div className="item-thumbnail">
                    <span className="thumbnail-icon">{resource.thumbnail}</span>
                  </div>
                  <div className="item-info">
                    <h4>{resource.title}</h4>
                    <p>by {resource.author}</p>
                    <div className="item-meta">
                      <span>{resource.category}</span>
                      <span>•</span>
                      <span>{resource.downloads} downloads</span>
                      <span>•</span>
                      <span>Uploaded today</span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleOpenResource(resource)}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleShareResource(resource)}
                    >
                      Share
                    </button>
                  </div>
                </div>
              ))}
            {resources.filter(
              (r) => r.uploadDate === new Date().toISOString().split("T")[0]
            ).length === 0 && (
              <div className="empty-state">
                <p>
                  No uploaded resources yet. Upload your first resource to share
                  with the community!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="e-library">
      <div className="page-header">
        <h1>E-Library & Digital Resources</h1>
        <p>
          AI-powered search, digital content management, and interactive
          learning resources
        </p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "search" && renderAISearch()}
        {activeTab === "browse" && renderBrowseLibrary()}
        {activeTab === "upload" && renderUploadContent()}
        {activeTab === "my-library" && renderMyLibrary()}
      </div>

      {/* Resource Viewer Modal */}
      {selectedResource && (
        <div className="modal-overlay">
          <div className="modal resource-viewer">
            <div className="modal-header">
              <h3>{selectedResource.title}</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedResource(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="resource-viewer-content">
                <div className="viewer-sidebar">
                  <div className="resource-details">
                    <h4>Details</h4>
                    <div className="detail-item">
                      <strong>Author:</strong> {selectedResource.author}
                    </div>
                    <div className="detail-item">
                      <strong>Category:</strong> {selectedResource.category}
                    </div>
                    <div className="detail-item">
                      <strong>Grade:</strong> {selectedResource.grade}
                    </div>
                    <div className="detail-item">
                      <strong>Size:</strong> {selectedResource.size}
                    </div>
                    <div className="detail-item">
                      <strong>Rating:</strong> {selectedResource.rating}/5
                    </div>
                    <div className="detail-item">
                      <strong>Downloads:</strong> {selectedResource.downloads}
                    </div>
                  </div>

                  <div className="resource-actions-sidebar">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleModalDownload(selectedResource)}
                    >
                      <Download size={16} />
                      Download
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleModalBookmark(selectedResource)}
                    >
                      <Bookmark size={16} />
                      {selectedResource.isBookmarked
                        ? "Unbookmark"
                        : "Bookmark"}
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => handleShareResource(selectedResource)}
                    >
                      <Share size={16} />
                      Share
                    </button>
                  </div>
                </div>

                <div className="viewer-main">
                  <div className="resource-preview">
                    {selectedResource.type === "video" && (
                      <div className="video-player">
                        <div className="video-placeholder">
                          <Play size={48} />
                          <p>Video Player</p>
                        </div>
                      </div>
                    )}
                    {selectedResource.type === "ebook" && (
                      <div className="ebook-viewer">
                        <div className="ebook-placeholder">
                          <BookOpen size={48} />
                          <p>E-book Viewer</p>
                        </div>
                      </div>
                    )}
                    {selectedResource.type === "audio" && (
                      <div className="audio-player">
                        <div className="audio-placeholder">
                          <Music size={48} />
                          <p>Audio Player</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="resource-description">
                    <h4>Description</h4>
                    <p>{selectedResource.description}</p>
                  </div>

                  <div className="resource-tags-full">
                    <h4>Tags</h4>
                    <div className="tags-container">
                      {selectedResource.tags.map((tag, index) => (
                        <span key={index} className="tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ELibrary;
